import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import {
	Button,
	Calendar,
	DrawerBodyWrapper,
	DrawerFooterWrapper,
	H4,
	H5,
	Input,
	RadioButton,
	Select,
	Textarea,
	TOAST_TYPE,
	UploadButton,
} from '@base'
import { CREDIT_LIMIT_STATUS, CREDIT_LIMIT_TYPES, MODAL_TYPES, strings } from '@constants'
import { RadioButtonWrapper } from '@pages/Admin/Content/styles'
import { Label } from '../../styles'
import { Spacings } from '@styles'
import { getOrganisationKycData, uploadKYCDocument, createCreditLimit, uploadSupportedDoc } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleFileViewer, toggleModal } from '@data/state/action/root'
import { useForceUpdate } from '@hooks'
import { updateCreditLimit } from '@data/conn/put/creditLimit'

export const FormItemWrapper = styled.div`
	margin-top: ${Spacings.SPACING_2B};
	display: flex;
	flex-direction: row;
	align-items: center;
	&:first-of-type {
		margin-top: 0;
	}
	& > div {
		width: 100%;
	}
	${({ isRadio }) => {
		if (isRadio) {
			return css`
				gap: ${Spacings.SPACING_2B};
			`
		}
	}}
`

export const InputWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	flex-direction: column;
	&:nth-child(1) {
		margin-right: ${Spacings.SPACING_3B};
	}
`

const DocumentUploadWrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: ${Spacings.SPACING_5B};
	gap: ${Spacings.SPACING_3B};
`

const TitleWrapper = styled(H4)`
	margin-bottom: ${Spacings.SPACING_1B};
	font-weight: 400;
`
const DocumentWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
`

const HeaderWrapper = styled(H5)`
	align-self: center;
	flex: 1;
`
const HeaderSpanWrapper = styled.span`
	color: ${({ theme, color }) => (color ? theme.palette.background[color] : theme.palette.text.greydark)};
	font-size: ${Text.SMALL};
`

const ButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: ${Spacings.SPACING_3B};
`

const statusTypes = Object.entries(CREDIT_LIMIT_STATUS).map(([key, value]) => ({ key, label: value }))

const deriveFormState = (data) => {
	let _state = {
		user_detail: {
			id: 'user-detail',
			placeholder: strings('user', 'details'),
			label: strings('user', 'details'),
			value: {},
			primaryKey: 'user_id',
			displayKey: 'full_name',
			disabled: false,
			required: false,
		},
		type: {
			value: CREDIT_LIMIT_TYPES.ADHOC,
		},
		sanction_date: {
			id: 'sanction-date',
			label: strings('sanction_date'),
			placeholder: strings('enter', 'sanction_date'),
			value: new Date().getTime(),
			disabled: !!data,
			required: false,
		},
		expiry_date: {
			id: 'expiry_date',
			label: strings('expiry_date'),
			placeholder: strings('expiry_date'),
			value: new Date().getTime(),
			disabled: !!data,
			required: false,
		},
		status: {
			id: 'status',
			label: strings('status'),
			placeholder: strings('select', 'status'),
			value: statusTypes.find((item) => item.label === CREDIT_LIMIT_STATUS.ACTIVE),
			data: statusTypes,
			primaryKey: 'key',
			displayKey: 'label',
			disabled: !!data,
			required: false,
		},
		commodity: {
			id: 'commodity',
			label: strings('commodity'),
			placeholder: strings('select', 'commodity'),
			value: '',
			primaryKey: 'commodity_id',
			displayKey: 'commodity_name',
			required: false,
			disabled: false,
		},
		amount: {
			id: 'amount',
			label: strings('amount'),
			placeholder: strings('enter', 'amount'),
			value: '',
			disabled: !!data,
			required: true,
		},
		aadhaar: {
			id: 'aadhaar',
			value: '',
			disabled: !!data,
			required: false,
		},
		pan_doc: {
			id: 'pan-doc',
			value: '',
			disabled: !!data,
			required: false,
		},
		bank_statement: {
			id: 'bank-statement',
			value: '',
			disabled: !!data,
			required: false,
		},
		financial_document: {
			id: 'financial-document',
			value: '',
			disabled: !!data,
			required: false,
		},
		supported_documents: {
			id: 'supported-document',
			value: [],
			disabled: !!data,
			required: false,
		},
		gst_certificate: {
			id: 'gst-cerificate',
			value: '',
			disabled: !!data,
			required: false,
		},
	}

	if (data) {
		_state.type.value = data?.type
		_state.status.value = statusTypes.find((item) => item.key === data?.status)
		_state.amount.value = data?.amount
		_state.sanction_date.value = data?.sanction_date
		_state.expiry_date.value = data?.expiry_date
		_state.supported_documents.value = data?.supported_documents
		_state.commodity.value = data?.commodity
	}
	return _state
}

const AddCreditLimit = ({ data, orgId, userData, outletUpdate, isEdit = false, commodities = [] }) => {
	const dispatch = useDispatch()
	const [forceUpdate, update] = useForceUpdate()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [deleteMessage, setDeleteMessage] = useState('')

	const [formState, setFormState] = useState(deriveFormState(data))
	const [curDocType, setCurDocType] = useState('')

	const setKycData = (data) => {
		setFormState((_state) => {
			return {
				..._state,
				pan_doc: {
					..._state['pan_doc'],
					value: data?.kyc_documents?.pan ?? '',
				},
				aadhaar: {
					..._state['aadhaar'],
					value: data?.kyc_documents?.aadhaar ?? '',
				},
				bank_statement: {
					..._state['bank_statement'],
					value: data?.kyc_documents?.bank_statement ?? '',
				},
				gst_certificate: {
					..._state['gst_certificate'],
					value: data?.kyc_documents?.gst_certificate ?? '',
				},
				financial_document: {
					..._state['financial_document'],
					value: data?.kyc_documents?.financial_document ?? '',
				},
			}
		})
	}

	useEffect(() => {
		setFormState(deriveFormState(data))
	}, [forceUpdate, data])
	useEffect(() => {
		getOrganisationKycData(orgId)
			.then((response) => {
				setKycData(response)
			})
			.catch((error) => {
				console.log(error)
			})
	}, [forceUpdate, orgId])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value,
			},
		}))
	}

	const downloadDocument = (filePath) => {
		dispatch(toggleFileViewer(true, { files: filePath }))
	}
	const uploadFileHandler =
		(key, typeOfProperty = null) =>
		(file) => {
			dispatch(showLoader(true))
			uploadKYCDocument(file, key, orgId, typeOfProperty)
				.then((res) => {
					dispatch(showToast(true, strings('upload_success'), { type: TOAST_TYPE.SUCCESS }))
					setKycData(res)
				})
				.catch((err) => {
					dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
					console.log(err)
				})
				.finally(() => {
					update()
					dispatch(showLoader(false))
				})
		}

	const submitHandler = () => {
		createCreditLimit({
			amount: formState?.amount?.value,
			created_at: new Date().getTime(),
			created_by: loggedInUser.getUserDetails()?.user_id,
			expiry_date: formState?.expiry_date?.value,
			sanction_date: formState?.sanction_date?.value,
			status: formState?.status?.value?.key,
			type: formState?.type?.value,
			user_details: userData,
			supported_documents: formState?.supported_documents?.value?.length > 0 ? formState?.supported_documents?.value : null,
			commodity: formState?.commodity?.value !== '' ? formState?.commodity?.value : null,
		})
			.then((response) => {
				dispatch(showToast(true, strings('msg_credit_limit_added_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				console.log(error)
				dispatch(showToast(true, strings('msg_credit_limit_added_fail'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				outletUpdate()
				dispatch(toggleDrawer(false))
			})
	}

	const supportDocUploader = (file) => {
		dispatch(showLoader(true))
		uploadSupportedDoc(file)
			.then((response) => {
				let _supDoc = formState.supported_documents?.value ?? []
				_supDoc.push({ type: curDocType, path: response })
				updateState('supported_documents', _supDoc)
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				setCurDocType('')
				dispatch(showLoader(false))
			})
	}

	const deleteSupportDoc = (index) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings('msg_are_you_sure_want_to_delete_doc'),
				confirmAction: (actionType) => {
					dispatch(showLoader(true))
					if (actionType) {
						const _supDocList = formState.supported_documents.value?.filter((item, _index) => _index !== index)
						updateState('supported_documents')(_supDocList)
					}
					dispatch(showLoader(false))
					dispatch(toggleModal(false))
				},
			})
		)
	}

	const disabled = useMemo(() => {
		return Object.values(formState).some((d) => d.required && !d.value) || curDocType !== ''
	}, [formState, curDocType])

	const SingleDocumentUpload = ({ type, file = null, mandatory = false, title, disabled = false }) => {
		return (
			<DocumentWrapper>
				<HeaderWrapper>
					{title} {mandatory && <HeaderSpanWrapper color='redLight'>{strings(['(', 'required', ')'])}</HeaderSpanWrapper>}
				</HeaderWrapper>
				<ButtonWrapper>
					<Button xs type='secondary' disabled={!file} onClick={() => downloadDocument(file)}>
						{strings('view')}
					</Button>

					<UploadButton
						xs
						variant='destructive'
						disabled={disabled}
						label={!file ? strings('upload') : strings('change')}
						onChange={uploadFileHandler(type)}
					/>
				</ButtonWrapper>
			</DocumentWrapper>
		)
	}

	const deleteHandler = () => {
		dispatch(showLoader(true))
		updateCreditLimit({ ...data, status: 'DELETE', deleted_on: new Date().getTime() })
			.then((response) => {
				dispatch(showToast(true, strings('msg_credit_limit_deleted_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				let error_msg = strings('msg_credit_limit_deleted_fail')
				if (error.client_error_code === 'E_002' && error?.message) {
					error_msg = error?.message
				}
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				outletUpdate()
				dispatch(toggleDrawer(false))
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<FormItemWrapper>
					<InputWrapper>
						<Calendar
							label={formState.sanction_date?.label}
							id={formState.sanction_date?.id}
							value={formState.sanction_date?.value}
							onChange={updateState('sanction_date')}
							disabled={formState.sanction_date?.disabled}
						/>
					</InputWrapper>
					<InputWrapper>
						<Calendar
							label={formState.expiry_date?.label}
							id={formState.expiry_date?.id}
							value={formState.expiry_date?.value}
							onChange={updateState('expiry_date')}
							disabled={formState.expiry_date?.disabled}
						/>
					</InputWrapper>
				</FormItemWrapper>
				<FormItemWrapper>
					<Select
						id={formState?.status?.id}
						label={formState?.status?.label}
						placeholder={formState?.status?.placeholder}
						value={formState?.status?.value}
						data={formState?.status?.data}
						primaryKey={formState?.status?.primaryKey}
						displayKey={formState?.status?.displayKey}
						disabled={formState?.status?.disabled}
						required={formState?.status?.required}
						onChange={updateState('status')}
					/>
				</FormItemWrapper>
				<FormItemWrapper>
					<Input
						id={formState.amount?.id}
						type='number'
						label={formState?.amount?.label}
						placeholder={formState?.amount?.placeholder}
						value={formState?.amount?.value}
						disabled={formState?.amount?.disabled}
						required={formState?.amount?.required}
						onChange={updateState('amount')}
					/>
				</FormItemWrapper>
				<FormItemWrapper>
					<Select
						id={formState?.commodity?.id}
						label={formState?.commodity?.label}
						placeholder={formState?.commodity?.placeholder}
						value={formState?.commodity?.value ?? {}}
						data={commodities}
						primaryKey={formState?.commodity?.primaryKey}
						displayKey={formState?.commodity?.displayKey}
						disabled={formState?.commodity?.disabled}
						required={formState?.commodity?.required}
						onChange={updateState('commodity')}
					/>
				</FormItemWrapper>
				<FormItemWrapper isRadio>
					<div>{strings('type', ':')}</div>
					<RadioButtonWrapper>
						<RadioButton
							id='adhoc-radio'
							size='1.125'
							checked={formState?.type?.value === CREDIT_LIMIT_TYPES.ADHOC}
							value={CREDIT_LIMIT_TYPES.ADHOC}
							onChange={(e) => updateState('type')(e.target.value)}
							disabled={isEdit}
						/>
						<Label id='adhoc-radio' margin={`0 ${Spacings.SPACING_1B}`}>
							{strings('ADHOC')}
						</Label>
					</RadioButtonWrapper>
					<RadioButtonWrapper>
						<RadioButton
							id='enhancement-radio'
							size='1.125'
							checked={formState?.type?.value === CREDIT_LIMIT_TYPES.ENHANCEMENT}
							value={CREDIT_LIMIT_TYPES.ENHANCEMENT}
							onChange={(e) => updateState('type')(e.target.value)}
							disabled={isEdit}
						/>
						<Label id='enhancement-radio' margin={`0 ${Spacings.SPACING_1B}`}>
							{strings('enhancement')}
						</Label>
					</RadioButtonWrapper>
					<RadioButtonWrapper>
						<RadioButton
							id='base-radio'
							size='1.125'
							checked={formState?.type?.value === CREDIT_LIMIT_TYPES.BASE}
							value={CREDIT_LIMIT_TYPES.BASE}
							onChange={(e) => updateState('type')(e.target.value)}
							disabled={isEdit}
						/>
						<Label id='base-radio' margin={`0 ${Spacings.SPACING_1B}`}>
							{strings('base')}
						</Label>
					</RadioButtonWrapper>
					<RadioButtonWrapper>
						<RadioButton
							id='base-radio'
							size='1.125'
							checked={formState?.type?.value === CREDIT_LIMIT_TYPES.EXPIRED}
							value={CREDIT_LIMIT_TYPES.EXPIRED}
							onChange={(e) => updateState('type')(e.target.value)}
							disabled={isEdit}
						/>
						<Label id='base-radio' margin={`0 ${Spacings.SPACING_1B}`}>
							{strings('expired')}
						</Label>
					</RadioButtonWrapper>
				</FormItemWrapper>

				<DocumentUploadWrapper>
					<TitleWrapper>{strings('msg_upload_document', 'director')}</TitleWrapper>
					<FormItemWrapper>
						<SingleDocumentUpload
							type='pan'
							title={strings('pan')}
							mandatory={formState?.pan_doc?.required}
							file={formState?.pan_doc?.value}
							disabled={formState?.pan_doc?.disabled}
						/>
					</FormItemWrapper>
					<FormItemWrapper>
						<SingleDocumentUpload
							type='aadhaar'
							title={strings('aadhaar')}
							mandatory={formState?.aadhaar?.required}
							file={formState?.aadhaar?.value}
							disabled={formState?.aadhaar?.disabled}
						/>
					</FormItemWrapper>
					<FormItemWrapper>
						<SingleDocumentUpload
							type='gst_certificate'
							title={strings('gst')}
							mandatory={formState?.gst_certificate?.required}
							file={formState?.gst_certificate?.value}
							disabled={formState.gst_certificate?.disabled}
						/>
					</FormItemWrapper>
					<FormItemWrapper>
						<SingleDocumentUpload
							type='bank_statement'
							title={strings('bank', 'statement')}
							mandatory={formState?.bank_statement?.required}
							file={formState?.bank_statement?.value}
							disabled={formState?.bank_statement?.disabled}
						/>
					</FormItemWrapper>
					<FormItemWrapper>
						<SingleDocumentUpload
							type='financial_document'
							title={strings('financial', 'document')}
							mandatory={formState?.financial_document?.required}
							file={formState?.financial_document?.value}
							disabled={formState?.financial_document?.disabled}
						/>
					</FormItemWrapper>
				</DocumentUploadWrapper>
				<DocumentUploadWrapper>
					{(!isEdit || (isEdit && formState?.supported_documents?.value?.length > 0)) && (
						<TitleWrapper>{strings('supported', 'documents')}</TitleWrapper>
					)}
					{formState.supported_documents?.value?.map((item, index) => (
						<FormItemWrapper key={`sup-doc-${index}`}>
							<DocumentWrapper>
								<HeaderWrapper>{item.type}</HeaderWrapper>
								<ButtonWrapper>
									<Button xs type='secondary' onClick={() => downloadDocument(item?.path)}>
										{strings('view')}
									</Button>
									<Button
										xs
										variant='destructive'
										onClick={() => {
											deleteSupportDoc(index)
										}}
										disabled={isEdit}
									>
										{strings('delete')}
									</Button>
								</ButtonWrapper>
							</DocumentWrapper>
						</FormItemWrapper>
					))}
					{!isEdit && (
						<FormItemWrapper>
							<Input
								id='supported-documents'
								value={curDocType}
								type='text'
								label={strings('document', 'type')}
								placeholder={strings(['enter', 'document', 'type'])}
								onChange={(value) => setCurDocType(value)}
								// required={true}
							/>
							<DocumentWrapper>
								<UploadButton
									variant='destructive'
									xs
									label={strings('upload')}
									disabled={!curDocType}
									onChange={supportDocUploader}
								/>
							</DocumentWrapper>
						</FormItemWrapper>
					)}
				</DocumentUploadWrapper>
				{formState.type.value === 'ADHOC' && isEdit && (
					<FormItemWrapper>
						<Textarea
							id='delete-message'
							label={strings(['remark', 'to', 'delete'])}
							type='text'
							rows={3}
							placeholder={strings('msg_share_any_more_details')}
							value={deleteMessage}
							onChange={setDeleteMessage}
						/>
					</FormItemWrapper>
				)}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button xs onClick={submitHandler} disabled={disabled || isEdit}>
					{strings('add')}
				</Button>
				{data && formState.type.value === 'ADHOC' && (
					<Button xs onClick={deleteHandler} variant='destructive' disabled={!deleteMessage} margin={`0 auto 0 0`}>
						{strings('delete')}
					</Button>
				)}
			</DrawerFooterWrapper>
		</>
	)
}

export default AddCreditLimit
